import qs from 'qs';
import server from '../utils/server';
import { createSelector } from '@reduxjs/toolkit';
import { getEmployees } from './employeeState';

// we're using "null" to mean "do not display"
const filterKeyLabelsByFilterKey = {
  utf8: null,
  authenticity_token: null,
  search: 'Search',
  range_key_label: null,
  range_key: 'Date',
  'search-store_name': 'Store name search',
  store_name: 'Store name',
  'search-district_name': 'District name search',
  district_name: 'District name',
  'search-region_name': 'Region name search',
  region_name: 'Region name',
  brandings: 'Brands',
  hidden_phone: null,
  hidden_chat: null,
  channels: 'Channels',
  hidden_email: null,
  hidden_mobile: null,
  stars: 'Stars',
  'search-tag': 'Search',
  has_comments: 'Comments',
  no_comments: 'No comments',
  employee_ids: 'Employees',
  area_of_improvement_ids: 'Areas to improve',
  area_of_excellence_ids: 'Areas of excellence',
  include_deactivated: 'Include deactivated',
  ts: null,
};

export function streamFilterState(
  state = {
    serializedFilters: '',
  },
  action
) {
  switch (action.type) {
    case 'DETERMINED_SERIALIZED_SOLICITATION_STREAM_FILTERS':
      return {
        ...state,
        serializedFilters: action.value.serializedFilters,
      };
    default: {
      return state;
    }
  }
}

const EMPTY_FILTERS = [];

const selectSerializedFilters = state => state.streamFilterState.serializedFilters;
const selectEmployeesById = state => state.employeeState.employeesById;
const selectBrandingsById = state => state.brandingState.brandingsById;
const selectAreasOfImprovementById = state => state.areasOfImprovementState.areasOfImprovementById;
const selectAreasOfExcellenceById = state => state.areasOfExcellenceState.areasOfExcellenceById;

export const getSolicitationFilters = createSelector(
  [
    selectSerializedFilters,
    selectEmployeesById,
    selectBrandingsById,
    selectAreasOfImprovementById,
    selectAreasOfExcellenceById,
  ],
  (
    serializedFilters,
    employeesById,
    brandingsById,
    areasOfImprovementById,
    areasOfExcellenceById
  ) => {
    if (!serializedFilters) {
      return EMPTY_FILTERS;
    }

    const filterValuesByKey = qs.parse(serializedFilters);

    const filters = [];
    Object.entries(filterValuesByKey).forEach(([filterKey, filterValue]) => {
      if (!filterValue || filterValue === '0') {
        return;
      }
      if (filterKeyLabelsByFilterKey[filterKey] === null) {
        return;
      }
      const filterKeyLabel = filterKeyLabelsByFilterKey[filterKey] || filterKey;
      let filterValueLabel = filterValue;

      switch (filterKey) {
        case 'has_comments':
          if (filterValueLabel !== '0') {
            filterValueLabel = 'Yes';
          }
          break;
        case 'no_comments':
          if (filterValueLabel !== '0') {
            filterValueLabel = '';
          }
          break;
        case 'include_deactivated':
          filterValueLabel = '';
          break;
        case 'brandings':
          if (filterValue.length > 1) {
            filterValueLabel = 'Multiple';
          } else {
            const branding = brandingsById[filterValue[0]];
            filterValueLabel = branding ? branding.name : '';
          }
          break;
        case 'area_of_improvement_ids':
          if (filterValue.length > 1) {
            filterValueLabel = 'Multiple';
          } else {
            const area = areasOfImprovementById[filterValue[0]];
            filterValueLabel = area ? area.caption : '';
          }
          break;
        case 'area_of_excellence_ids':
          if (filterValue.length > 1) {
            filterValueLabel = 'Multiple';
          } else {
            const area = areasOfExcellenceById[filterValue[0]];
            filterValueLabel = area ? area.caption : '';
          }
          break;
        case 'employee_ids':
          filterValueLabel = (filterValueLabel || '').split(',');
          if (filterValueLabel.length > 1) {
            filterValueLabel = 'Multiple';
          } else {
            const [employeeId] = filterValueLabel;
            const employee = employeesById[employeeId];
            if (employee) {
              filterValueLabel = `${employee.last_name}, ${employee.first_name}`;
            } else {
              filterValueLabel = '';
            }
          }
          break;
        default:
        // ...we good
      }

      if (Array.isArray(filterValueLabel)) {
        filterValueLabel = filterValueLabel.join(', ');
      }

      filters.push({
        filterKey,
        filterValue,
        filterKeyLabel,
        filterValueLabel,
      });
    });

    return filters;
  }
);

let initialPopulationInitiated = false;

export function fetchFilterEntities() {
  return (dispatch, getState) => {
    const solicitationFilters = getSolicitationFilters(getState());
    if (!solicitationFilters.length) {
      return;
    }
    if (!initialPopulationInitiated) {
      server
        .get('/api/v1/filter_entities')
        .then(({ areasOfExcellence, areasOfImprovement, brandings }) => {
          dispatch({
            type: 'RECEIVED_AREAS_OF_IMPROVEMENT',
            value: { areasOfImprovement },
          });
          dispatch({
            type: 'RECEIVED_AREAS_OF_EXCELLENCE',
            value: { areasOfExcellence },
          });
          dispatch({
            type: 'RECEIVED_BRANDINGS',
            value: { brandings },
          });
        });
      initialPopulationInitiated = true;
    }
    const employeeFiltersWithoutLabels = solicitationFilters.filter(
      f => f.filterKey === 'employee_ids' && !f.filterValueLabel
    );
    const unloadedEmployeeDataIds = [];
    employeeFiltersWithoutLabels.forEach(filter => {
      filter.filterValue.split(',').forEach(employeeId => {
        unloadedEmployeeDataIds.push(employeeId);
      });
    });
    if (unloadedEmployeeDataIds.length) {
      dispatch(getEmployees({ employeeIds: unloadedEmployeeDataIds }));
    }
  };
}
